/**
 * Zapytania wyszukiwarki (TanStack Query v5).
 *
 * Dwa żądania, nie jedno — taki jest kontrakt backendu i taki jest sens degradacji:
 * `/api/search` odpowiada natychmiast (wyniki pełnotekstowe), a gdy odda
 * `semantic_pending: true`, część bliskoznaczną dogrywamy `/api/search/semantic`.
 * Użytkownik ma wyniki od razu, a to, co wymaga embeddera, dojeżdża pod kreską.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { SearchResponse } from './types'

/** Poniżej tylu znaków nie pytamy serwera — jedna litera pasuje do wszystkiego. */
export const MIN_QUERY_LENGTH = 2
/** Opóźnienie po ostatnim wciśniętym klawiszu. */
const DEBOUNCE_MS = 250

export const searchKeys = {
  all: ['search'] as const,
  query: (q: string) => ['search', 'query', q] as const,
  semantic: (q: string) => ['search', 'semantic', q] as const,
}

/** Wartość ustabilizowana po `delay` ms bez zmiany — żeby nie strzelać na każdą literę. */
export function useDebounced<T>(value: T, delay = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])
  return debounced
}

export function useSearch(query: string, enabled: boolean) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: searchKeys.query(trimmed),
    queryFn: () => api.post<SearchResponse>('/search', { query: trimmed }),
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    // Wyniki tej samej frazy nie zmieniają się co chwilę, a paleta bywa otwierana
    // i zamykana wielokrotnie pod rząd.
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  })
}

/**
 * Dogrywka sekcji „Wyniki bliskoznaczne". Wystartuje TYLKO wtedy, gdy pierwsze
 * żądanie zgłosiło `semantic_pending` — inaczej byłoby to drugie strzelenie w
 * embedder, który przed chwilą zdążył.
 */
export function useSemanticFollowUp(query: string, pending: boolean) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: searchKeys.semantic(trimmed),
    queryFn: () => api.post<SearchResponse>('/search/semantic', { query: trimmed }),
    enabled: pending && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
    // Embedder bywa wolny albo nieobecny; ponawianie tylko wydłuża oczekiwanie
    // na coś, co i tak jest dodatkiem do wyników już widocznych na ekranie.
    retry: false,
  })
}
